import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import logo from "../res/logo.png";

function News({ data, news }) {
  const others = useMemo(
    () =>
      news.filter(
        (n) => n.id && n.url && n.id !== data.id && n.url !== data.url,
      ),
    [news, data.id, data.url],
  );

  const paragraphs = useMemo(() => {
    const raw = data.newsBody != null ? String(data.newsBody) : "";
    return raw.split("\n").filter((line) => line.trim().length > 0);
  }, [data.newsBody]);

  useEffect(() => {
    document.title = data.title ? `${data.title} | PEACI` : "News | PEACI";
  }, [data.title]);

  return (
    <main
      className="w-full bg-stone-50 dark:bg-stone-900/30 py-8 sm:py-12"
      role="main"
    >
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
        <nav
          className="mb-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-stone-600 dark:text-stone-400"
          aria-label="Breadcrumb"
        >
          <Link
            to="/"
            className="font-medium text-sky-700 transition-colors hover:text-sky-600 hover:underline dark:text-sky-400 dark:hover:text-sky-300"
          >
            Home
          </Link>
          <span className="text-stone-400" aria-hidden>
            /
          </span>
          <Link
            to="/#home-news-heading"
            className="font-medium text-sky-700 transition-colors hover:text-sky-600 hover:underline dark:text-sky-400 dark:hover:text-sky-300"
          >
            News
          </Link>
          <span className="text-stone-400" aria-hidden>
            /
          </span>
          <span className="line-clamp-1 text-stone-500 dark:text-stone-500">
            {data.title}
          </span>
        </nav>

        <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-10">
          <article className="min-w-0 flex-1 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
            <div className="p-6 sm:p-8">
              <h1 className="text-2xl font-bold tracking-tight text-stone-900 dark:text-white sm:text-3xl lg:text-4xl">
                {data.title}
              </h1>
              {data.date ? (
                <p className="mt-2 text-sm font-medium text-sky-600 dark:text-sky-400">
                  {data.date}
                </p>
              ) : null}

              <div className="prose prose-stone mt-6 max-w-none dark:prose-invert">
                {paragraphs.length > 0 ? (
                  paragraphs.map((p, key) => (
                    <p
                      className="mb-3 text-base leading-relaxed text-stone-700 last:mb-0 dark:text-stone-300"
                      key={key}
                    >
                      {p}
                    </p>
                  ))
                ) : (
                  <p className="text-base text-stone-500 dark:text-stone-400">
                    No article text available.
                  </p>
                )}
              </div>
            </div>
          </article>

          <aside className="w-full shrink-0 lg:w-72 xl:w-80">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-none lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
              <header className="mb-4 shrink-0">
                <h2 className="text-lg font-bold text-stone-900 dark:text-white sm:text-xl">
                  More news
                </h2>
                <div
                  className="mt-1 h-0.5 w-16 rounded-full bg-sky-500"
                  aria-hidden
                />
              </header>

              {others.length === 0 ? (
                <p className="text-sm text-stone-500 dark:text-stone-400">
                  No other news to show.
                </p>
              ) : (
                <ul className="flex flex-col gap-2.5">
                  {others.map((val) => (
                    <li key={val.id || val.url}>
                      <Link
                        to={`/${val.url}`}
                        className="group block overflow-hidden rounded-lg border border-transparent p-2 transition-colors hover:border-sky-200 hover:bg-stone-50 dark:hover:border-sky-900/50 dark:hover:bg-gray-700/50"
                      >
                        <span className="line-clamp-2 text-sm font-semibold leading-snug text-stone-900 group-hover:text-sky-600 dark:text-white dark:group-hover:text-sky-400">
                          {val.title}
                        </span>
                        {val.date ? (
                          <span className="mt-1 block text-xs text-sky-600 dark:text-sky-400">
                            {val.date}
                          </span>
                        ) : null}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

export default News;
